import React, { useEffect, useCallback, useMemo } from 'react';
import { Form, Input, Card, Button } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { notEmptyRule } from './validationUtils';
import ImageDisplayField from '../ImageDisplayField';
import SimpleEditor from '../../../../../../components/RichTextEditor/SimpleEditor';

const { TextArea } = Input;

const OurServicesSection = ({ 
  sectionKey, 
  sectionData, 
  uploadingImage, 
  uploadProps, 
  handleImageUploadForItem,
  currentLanguage,
  form,
  pageSlug
}) => {
  const { t } = useTranslation();
  
  // Create dynamic field names based on current language
  const langKey = currentLanguage.toLowerCase();
  const getFieldName = useCallback((field) => ['content', langKey, sectionKey, field], [langKey, sectionKey]);

  // Define default structure for Our Services items
  const defaultItem = useMemo(() => ({
    title: '',
    description: '',
    image: '',
    image_large: ''
  }), []);

  useEffect(() => {
    const itemsFieldName = getFieldName('items');
    const currentItems = form.getFieldValue(itemsFieldName) || [];
    
    if (currentItems.length === 0) {
      form.setFieldsValue({
        [itemsFieldName.join('.')]: [defaultItem]
      });
    }
  }, [defaultItem, form, getFieldName]);

  return (
    <>
      {/* Regular section fields */}
      {sectionData.title !== undefined && (
        <Form.Item
          name={getFieldName('title')}
          label={t('Titolo')}
          rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
        >
          <Input placeholder={t('Inserisci il titolo')} />
        </Form.Item>
      )}

      {sectionData.description !== undefined && (
        <Form.Item
          name={getFieldName('description')}
          label={t('Descrizione')}
          rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
        >
          {pageSlug === 'services' || pageSlug === 'gift-card' ? (
            <SimpleEditor rows={3} placeholder={t('Inserisci la descrizione')} />
          ) : (
            <TextArea rows={3} placeholder={t('Inserisci la descrizione')} />
          )}
        </Form.Item>
      )}

      {/* Items section */}
      <Form.List name={getFieldName('items')}>
        {(fields, { add, remove }) => (
          <>
            {fields.map(({ key, name, ...restField }) => {
              const sampleItem = sectionData.items?.[0] || defaultItem;
              
              return (
                <Card
                  key={key}
                  size='small'
                  style={{ marginBottom: 16 }}
                  extra={
                    fields.length > 1 && (
                      <Button
                        type='text'
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                        size='small'
                      >
                        {t('Rimuovi')}
                      </Button>
                    )
                  }
                >
                  {sampleItem.title !== undefined && (
                    <Form.Item
                      {...restField}
                      name={[name, 'title']}
                      label={t('Titolo')}
                      rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                    >
                      <Input placeholder={t('Inserisci il titolo')} />
                    </Form.Item>
                  )}

                  {sampleItem.description !== undefined && (
                    <Form.Item
                      {...restField}
                      name={[name, 'description']}
                      label={t('Descrizione')}
                    >
                      {pageSlug === 'services' || pageSlug === 'gift-card' ? (
                        <SimpleEditor rows={2} placeholder={t('Inserisci la descrizione')} />
                      ) : (
                        <TextArea rows={2} placeholder={t('Inserisci la descrizione')} />
                      )}
                    </Form.Item>
                  )}

                  {sampleItem.image !== undefined && (
                    <Form.Item
                      {...restField}
                      name={[name, 'image']}
                      label='Immagine miniatura (jpg, png, gif)'
                      rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                    >
                      <ImageDisplayField
                        name={[...getFieldName('items'), name, 'image']}
                        label={t('Immagine')}
                        uploadingImage={uploadingImage}
                        uploadProps={uploadProps}
                        onUpload={(file) => handleImageUploadForItem(file, sectionKey, name)}
                        onRemove={() => {
                          const currentValues = form.getFieldsValue();
                          const fieldPath = ['content', currentLanguage.toLowerCase(), sectionKey, 'items', name, 'image'];
                          let current = currentValues;
                          for (let i = 0; i < fieldPath.length - 1; i++) {
                            if (!current[fieldPath[i]]) {
                              current[fieldPath[i]] = {};
                            }
                            current = current[fieldPath[i]];
                          }
                          current[fieldPath[fieldPath.length - 1]] = '';
                          form.setFieldsValue(currentValues);
                        }}
                      />
                    </Form.Item>
                  )}

                  {sampleItem.image_large !== undefined && (
                    <Form.Item
                      {...restField}
                      name={[name, 'image_large']}
                      label='Immagine hero (jpg, png, gif)'
                      rules={[notEmptyRule(t, 'Il campo non può essere vuoto')]}
                    >
                      <ImageDisplayField
                        name={[...getFieldName('items'), name, 'image_large']}
                        label='Immagine hero'
                        uploadingImage={uploadingImage}
                        uploadProps={uploadProps}
                        onUpload={(file) => handleImageUploadForItem(file, sectionKey, name, 'image_large')}
                        onRemove={() => {
                          const currentValues = form.getFieldsValue();
                          const fieldPath = ['content', currentLanguage.toLowerCase(), sectionKey, 'items', name, 'image_large'];
                          let current = currentValues;
                          for (let i = 0; i < fieldPath.length - 1; i++) {
                            if (!current[fieldPath[i]]) {
                              current[fieldPath[i]] = {};
                            }
                            current = current[fieldPath[i]];
                          }
                          current[fieldPath[fieldPath.length - 1]] = '';
                          form.setFieldsValue(currentValues);
                        }}
                      />
                    </Form.Item>
                  )}
                </Card>
              );
            })}
            <Button
              type='dashed'
              icon={<PlusOutlined />}
              onClick={() => add({ ...defaultItem })}
              style={{ width: '100%' }}
            >
              {t('Aggiungi elemento')}
            </Button>
          </>
        )}
      </Form.List>
    </>
  );
};

export default OurServicesSection; 